import React, { useEffect, useMemo, useState } from 'react';

import { Filter as FilterIcon } from '@styled-icons/fa-solid/Filter';
import { api } from 'services/api';
import { toast } from 'shared/toast';

import {
  Button,
  Flex,
  FormControl,
  FormLabel,
  Grid,
  Icon,
  Input,
} from '@chakra-ui/react';

import OwnSelect from 'components/OwnSelect';

import { UnitMeasure } from 'types/base';
import { Budget } from 'types/budget';

type SelectedOption = {
  value: string;
  label: string;
};

const INPUT_CLASSIFICATION_TYPE_OPTIONS = [
  {
    value: 'equipment',
    label: 'Equipamento',
  },
  {
    value: 'labor',
    label: 'Mão de obra',
  },
  {
    value: 'material',
    label: 'Material',
  },
  {
    value: 'thirdPartyService',
    label: 'Serviço de terceiros',
  },
  {
    value: 'other',
    label: 'Outro',
  },
];

type FiltersPanelProps = {
  budgetBases: Budget['bases'];
  selectedBudgetBaseId: number | null;
  selectedClassificationType: string | null;
  selectedUnitMeasureId: number | null;
  selectedVersionDescription: string;
  isLoading: boolean;
  onChangeBudgetBase: (baseId: number | null) => void;
  onChangeClassificationType: (classificationType: string | null) => void;
  onChangeUnitMeasureId: (unitMeasureId: number | null) => void;
  onSearch: () => void;
};

const FiltersPanel: React.FC<FiltersPanelProps> = ({
  budgetBases,
  selectedBudgetBaseId,
  selectedClassificationType,
  selectedUnitMeasureId,
  selectedVersionDescription,
  isLoading,
  onChangeBudgetBase,
  onChangeClassificationType,
  onChangeUnitMeasureId,
  onSearch,
}) => {
  const baseOptions = useMemo(() => {
    return budgetBases.map((base) => ({
      value: String(base.id),
      label: base.description || base.version.base.description,
    }));
  }, [budgetBases]);

  const selectedBaseOption = useMemo(() => {
    if (!selectedBudgetBaseId) {
      return [];
    }

    return baseOptions.filter(
      (option) => Number(option.value) === selectedBudgetBaseId,
    );
  }, [baseOptions, selectedBudgetBaseId]);

  const handleBaseChange = (selected: SelectedOption | null): void => {
    onChangeBudgetBase(selected ? Number(selected.value) : null);
  };

  const selectedClassificationTypeOption = useMemo(() => {
    if (!selectedClassificationType) {
      return [];
    }

    return INPUT_CLASSIFICATION_TYPE_OPTIONS.filter(
      (option) => option.value === selectedClassificationType,
    );
  }, [selectedClassificationType]);

  const handleClassificationTypeChange = (
    selected: SelectedOption | null,
  ): void => {
    onChangeClassificationType(selected ? selected.value : null);
  };

  useEffect(() => {
    if (INPUT_CLASSIFICATION_TYPE_OPTIONS.length === 1) {
      const [singleOption] = INPUT_CLASSIFICATION_TYPE_OPTIONS;
      onChangeClassificationType(singleOption.value);
    }
  }, [onChangeClassificationType]);

  const [unitMeasures, setUnitMeasures] = useState<UnitMeasure[]>([]);
  const [isLoadingUnitMeasures, setIsLoadingUnitMeasures] = useState(false);

  useEffect(() => {
    const getUnitMeasures = async (): Promise<void> => {
      setIsLoadingUnitMeasures(true);

      try {
        const { data: response } = await api.get('/unit/measure/list', {
          params: {
            'filter[no_organization]': '.',
          },
        });
        const unitMeasuresLoaded = response.data || [];
        setUnitMeasures(unitMeasuresLoaded);
      } catch (error) {
        const errorWithResponse = error as {
          response?: { data?: { message?: string } };
        };

        toast({
          description:
            errorWithResponse.response?.data?.message ||
            'Houve um erro ao carregar as unidades de medida!',
          status: 'error',
        });
      } finally {
        setIsLoadingUnitMeasures(false);
      }
    };

    getUnitMeasures();
  }, []);

  const unitMeasureOptions = useMemo(() => {
    return unitMeasures.map((unitMeasure) => ({
      value: String(unitMeasure.id),
      label: unitMeasure.description,
    }));
  }, [unitMeasures]);

  const selectedUnitMeasureOption = useMemo(() => {
    if (!selectedUnitMeasureId) {
      return [];
    }

    return unitMeasureOptions.filter(
      (option) => Number(option.value) === selectedUnitMeasureId,
    );
  }, [selectedUnitMeasureId, unitMeasureOptions]);

  const handleUnitMeasureChange = (selected: SelectedOption | null): void => {
    onChangeUnitMeasureId(selected ? Number(selected.value) : null);
  };

  useEffect(() => {
    if (unitMeasureOptions.length === 1) {
      const [singleOption] = unitMeasureOptions;
      onChangeUnitMeasureId(Number(singleOption.value));
    }
  }, [onChangeUnitMeasureId, unitMeasureOptions]);

  return (
    <Flex
      mb={3}
      as="form"
      alignItems="center"
      justifyContent="center"
      flexDirection="column"
      sx={{
        '> *': {
          marginY: 1,
        },
      }}
    >
      <Grid
        w="100%"
        templateColumns="repeat(auto-fit, minmax(300px, 1fr))"
        columnGap={2}
        rowGap={4}
      >
        <FormControl>
          <FormLabel>Base</FormLabel>

          <OwnSelect
            placeholder="Selecione"
            options={baseOptions}
            value={selectedBaseOption}
            onChange={handleBaseChange}
            isClearable
            isDisabled={baseOptions.length === 0}
          />
        </FormControl>

        <FormControl>
          <FormLabel>Referência</FormLabel>

          <Input
            value={selectedVersionDescription}
            placeholder="Selecione uma base"
            isReadOnly
            _readOnly={{ background: 'white' }}
          />
        </FormControl>

        <FormControl>
          <FormLabel>Tipo de insumo</FormLabel>

          <OwnSelect
            placeholder="Selecione"
            options={INPUT_CLASSIFICATION_TYPE_OPTIONS}
            value={selectedClassificationTypeOption}
            onChange={handleClassificationTypeChange}
            isClearable
            isDisabled={INPUT_CLASSIFICATION_TYPE_OPTIONS.length === 0}
          />
        </FormControl>

        <FormControl>
          <FormLabel>Unidade de medida</FormLabel>

          <OwnSelect
            placeholder="Selecione"
            options={unitMeasureOptions}
            value={selectedUnitMeasureOption}
            onChange={handleUnitMeasureChange}
            isClearable
            isLoading={isLoadingUnitMeasures}
            isDisabled={unitMeasureOptions.length === 0}
          />
        </FormControl>
      </Grid>

      <Flex mt={3} w="100%" justifyContent="flex-end">
        <Button
          size="sm"
          w="100%"
          colorScheme="blue"
          onClick={onSearch}
          isLoading={isLoading}
          leftIcon={<Icon as={FilterIcon} />}
        >
          Filtrar
        </Button>
      </Flex>
    </Flex>
  );
};

export default FiltersPanel;
